import React, { useState } from 'react';
import { ArrowLeft, Trash2, Heart, Activity, Droplets, Droplet, Shield, Syringe, Package2, TrendingUp } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { formatINR } from '@/data/mockData';

const CAT_ICON_MAP = {
  'Fertility':          { Icon: Heart,      color: '#E91E63', bg: '#FFF0F3' },
  "Women's Wellness":   { Icon: Activity,   color: '#9C27B0', bg: '#F3E5F5' },
  'Hormonal Support':   { Icon: Droplets,   color: '#3F51B5', bg: '#E8EAF6' },
  'Gynae Hematinics':   { Icon: Droplet,    color: '#F44336', bg: '#FFEBEE' },
  'Bone & Supportive':  { Icon: Shield,     color: '#388E3C', bg: '#E8F5E9' },
  'Injectable Range':   { Icon: Syringe,    color: '#FF5722', bg: '#FBE9E7' },
  'Miscellaneous':      { Icon: Package2,   color: '#607D8B', bg: '#ECEFF1' },
  'Top Movers':         { Icon: TrendingUp, color: '#FF9800', bg: '#FFF3E0' },
};

export default function CheckoutScreen() {
  const { goBack, navigate, screenParams } = useApp();
  const { product, qty: initialQty = 1 } = screenParams || {};

  const [cart, setCart] = useState(
    product ? [{ product, qty: initialQty }] : []
  );

  const updateQty = (idx, delta) => {
    setCart(prev => prev.map((item, i) =>
      i === idx ? { ...item, qty: Math.max(1, item.qty + delta) } : item
    ));
  };

  const removeItem = (idx) => {
    setCart(prev => prev.filter((_, i) => i !== idx));
  };

  const totalItems = cart.length;
  const totalQty = cart.reduce((sum, item) => sum + item.qty, 0);
  const grossTotal = cart.reduce((sum, item) => {
    const pts = Math.round(item.product.mrp * 0.82);
    return sum + pts * item.qty;
  }, 0);

  return (
    <div className="min-h-screen bg-[#F5F6FA] pb-6">
      {/* Header */}
      <div className="bg-white px-4 pt-5 pb-3 border-b border-gray-100 flex items-center">
        <button
          data-testid="checkout-back-btn"
          onClick={goBack}
          className="w-9 h-9 flex items-center justify-center tap-scale"
        >
          <ArrowLeft size={22} color="#374151" />
        </button>
        <h1
          data-testid="checkout-title"
          className="font-black text-gray-900 text-center flex-1 uppercase"
          style={{ fontSize: '15px', letterSpacing: '0.12em' }}
        >
          CHECKOUT
        </h1>
        <div className="w-9 h-9" />
      </div>

      {/* Cart items */}
      <div className="px-4 pt-4 pb-2 flex flex-col gap-3">
        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Package2 size={48} color="#D1D5DB" className="mb-3" />
            <div className="font-semibold">Your cart is empty</div>
            <button
              onClick={goBack}
              className="mt-4 text-[#FF5722] font-bold text-sm tap-scale"
            >
              Add Products
            </button>
          </div>
        ) : (
          cart.map((item, idx) => {
            const pts = Math.round(item.product.mrp * 0.82);
            const total = pts * item.qty;
            const info = CAT_ICON_MAP[item.product.category] || { Icon: Package2, color: '#607D8B', bg: '#ECEFF1' };
            const { Icon } = info;
            return (
              <div
                key={idx}
                data-testid={`checkout-item-${idx}`}
                className="bg-white rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}
              >
                <div className="p-3 flex gap-3 items-start">
                  {/* Product image */}
                  <div
                    className="w-28 h-24 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: info.bg }}
                  >
                    <Icon size={48} color={info.color} strokeWidth={1.5} />
                  </div>
                  {/* Info + qty */}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-gray-900 leading-tight mb-0.5">
                      {item.product.name}
                    </div>
                    <div className="text-xs text-gray-500 mb-1">{item.product.pack}</div>
                    <div className="text-sm text-gray-600 mb-0.5">
                      M.R.P: ₹{formatINR(item.product.mrp)}
                    </div>
                    <div className="text-sm font-semibold text-green-600 mb-0.5">
                      Net PTS: ₹{formatINR(pts)} / unit
                    </div>
                    <div className="text-sm font-bold text-blue-600">
                      Total: ₹{formatINR(total)}
                    </div>
                  </div>
                  {/* Qty control */}
                  <div
                    className="flex items-center rounded-xl self-center"
                    style={{ border: '1.5px solid #D1D5DB' }}
                  >
                    <button
                      data-testid={`checkout-qty-dec-${idx}`}
                      onClick={() => updateQty(idx, -1)}
                      className="w-9 h-9 flex items-center justify-center text-gray-600 font-bold text-lg tap-scale"
                    >
                      −
                    </button>
                    <span
                      data-testid={`checkout-qty-val-${idx}`}
                      className="w-8 text-center font-bold text-sm text-gray-900"
                    >
                      {item.qty}
                    </span>
                    <button
                      data-testid={`checkout-qty-inc-${idx}`}
                      onClick={() => updateQty(idx, 1)}
                      className="w-9 h-9 flex items-center justify-center text-gray-600 font-bold text-lg tap-scale"
                    >
                      +
                    </button>
                  </div>
                </div>
                {/* Remove */}
                <button
                  data-testid={`checkout-remove-${idx}`}
                  onClick={() => removeItem(idx)}
                  className="w-full border-t border-gray-100 py-3 flex items-center justify-center gap-2 text-red-500 font-semibold text-sm tap-scale"
                >
                  <Trash2 size={14} />
                  Remove
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Price Summary */}
      {cart.length > 0 && (
        <div
          className="mx-4 mt-2 bg-white rounded-2xl p-4 mb-4"
          style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}
        >
          <div
            className="font-black text-gray-900 mb-4 tracking-widest uppercase"
            style={{ fontSize: '12px' }}
          >
            PRICE SUMMARY
          </div>
          <div className="flex justify-between items-center mb-2.5">
            <span className="text-sm text-gray-700 font-medium">Total Items</span>
            <span className="text-sm font-bold text-blue-600">
              {totalItems} Product{totalItems !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex justify-between items-center mb-2.5">
            <span className="text-sm text-gray-700 font-medium">Total Quantity:</span>
            <span className="text-sm font-semibold text-gray-900">{totalQty} Units</span>
          </div>
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm font-black text-gray-900">Gross Total</span>
            <span className="text-base font-black text-green-600">₹{formatINR(grossTotal)}</span>
          </div>
          <div className="flex justify-center">
            <div
              className="px-5 py-2 rounded-full text-sm font-semibold text-yellow-800"
              style={{ background: '#FFFDE7', border: '1px solid #FDD835' }}
            >
              +GST Extra as applicable
            </div>
          </div>
        </div>
      )}

      {/* Bottom Buttons */}
      {cart.length > 0 && (
        <div className="px-4 flex gap-3">
          <button
            data-testid="checkout-add-more-btn"
            onClick={goBack}
            className="flex-1 font-bold py-4 rounded-2xl tap-scale text-sm text-gray-700 bg-white"
            style={{ border: '2px solid #D1D5DB' }}
          >
            Add more
          </button>
          <button
            data-testid="checkout-confirm-btn"
            onClick={() => navigate('PURCHASE_SUCCESS')}
            className="flex-1 text-white font-black py-4 rounded-2xl tap-scale text-sm"
            style={{ background: '#FF7043' }}
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  );
}
